import React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Container, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { useConfirm } from 'hooks';
import Logs from 'components/Logs';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import { transformAsyncErrors } from 'utils/utils';
import { useHistory, useParams } from 'react-router-dom';
import { DocumentsActions } from 'state/modules/documents';
import VersionModal from 'components/Documents/VersionModal';
import DocumentModal from 'components/Documents/DocumentModal';
import VersionsWidget from 'components/Documents/VersionsListWidget';
import VersionDetailsWidget from 'components/Documents/VersionDetailsWidget';
import DocumentDetailsWidget from 'components/Documents/DocumentDetailsWidget';
import Grid from '@mui/material/Grid';

const ActionButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
  textTransform: 'none',
}));

const StyledContainer = styled(Container)({
  minWidth: '100%',
  margin: 0,
  padding: 0,
});

const DocumentsDetailsPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [asyncErrors, setAsyncErrors] = useState({});
  const [renameModal, setRenamModal] = useState(false);
  const [createVersionModal, setCreateVersionModal] = useState(false);
  const document = useSelector((state) => state.documents.byId[id] || {});
  const isUploading = useSelector((state) => state.documents.isUploading);

  const [toggleDeleteDocument, DeleteDocumentConfirm] = useConfirm({
    title: i18n.t('documents.delete_document'),
    message: i18n.t('documents.delete_document_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: useCallback(
      () =>
        dispatch(DocumentsActions.deleteDocument(id, () => history.push(buildUrl('/documents')))),
      [dispatch, history, id]
    ),
  });

  const toggleVersionModal = () => {
    if (createVersionModal) {
      setAsyncErrors({});
    }
    setCreateVersionModal(!createVersionModal);
  };
  const toggleRenameModal = () => {
    setRenamModal(!renameModal);
  };
  const onCreate = async (data) => {
    const response = await dispatch(DocumentsActions.createVersion(id, data));
    if (response.ok) {
      toggleVersionModal();
      history.push(buildUrl(`/documents/${id}/versions/${response.data.id}`));
    } else {
      setAsyncErrors({
        ...asyncErrors,
        createVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  /**
   * Rename document
   * @param {string} name
   */
  const onRename = async (document_type_id, description) => {
    const payload = { document_type_id, description };
    Object.keys(payload).forEach((key) => {
      if (!payload[key]) payload[key] = null;
    });
    await dispatch(DocumentsActions.updateDocument(id, payload));
    toggleRenameModal();
  };

  useEffect(() => {
    dispatch(DocumentsActions.fetchDocument(id));
    dispatch(DocumentsActions.resetCurrentVersion());
  }, [id, dispatch]);

  /**
   * Navigate to version details screen
   * @param {string} versionId Id of the version
   */
  const goToVersion = (versionId) => {
    history.push(buildUrl(`/documents/${id}/versions/${versionId ? versionId : ''}`));
  };

  /**
   * Handle tag click and navigate to link
   */
  const handleTagClick = () => {
    switch (document.source_type) {
      case 'User': {
        history.push(buildUrl(`/customers/${document.source_id}`));
        return;
      }
      case 'Vehicle': {
        history.push(buildUrl(`/vehicles/${document.source_id}`));
        return;
      }
      default:
        return;
    }
  };

  const onUpdateVersion = (data, versionId) => {
    dispatch(DocumentsActions.updateVersion(id, versionId, data, null, true));
  };

  const onUploadFile = (versionId, file) => {
    dispatch(DocumentsActions.uploadVersionsFile(id, versionId, file));
  };

  const onRemoveFile = (versionId) => {
    dispatch(DocumentsActions.deleteVersionsFile(id, versionId));
  };

  const menuItems = [
    {
      title: i18n.t('documents.create_new_version'),
      can: { name: 'document', action: 'create' },
      onClick: toggleVersionModal,
    },
  ];

  const getTags = () => {
    return document && document?.source_type && document?.source_type
      ? [
          {
            label: `${
              document.source_type === 'User' ? i18n.t('customers.customer') : document.source_type
            } #${document.source_id}`,
          },
        ]
      : [];
  };

  return (
    <Layout title={i18n.t('documents.documents_details')}>
      <StyledContainer>
        <Box sx={{ width: '100%' }}>
          <Stack
            direction={{ md: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'start', lg: 'center' }}
            sx={{ mb: 2 }}
          >
            <Title
              title={`${i18n.t('documents.documents_details')} ${
                document?.document_type?.name ? '- ' + document?.document_type?.name : ''
              }`}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              <ActionButton
                color="primary"
                variant="outlined"
                data-cy="create-document"
                onClick={toggleVersionModal}
              >
                {i18n.t('documents.create_new_version')}
              </ActionButton>
              {document.default?.id && (
                <ActionButton
                  color="primary"
                  variant="outlined"
                  data-cy="rename-document"
                  onClick={toggleRenameModal}
                >
                  {i18n.t('documents.edit_document')}
                </ActionButton>
              )}
              <ActionButton
                color="primary"
                variant="contained"
                data-cy="delete-document"
                onClick={toggleDeleteDocument}
              >
                {i18n.t('documents.delete_document')}
              </ActionButton>
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <DocumentDetailsWidget
                document={document}
                tags={getTags()}
                onTagClick={handleTagClick}
              />
              <Box mt={3} />
              <VersionsWidget documentId={id} menuItems={menuItems} />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <VersionDetailsWidget
                document={parseInt(id)}
                id={document?.default?.id}
                handleChange={onUpdateVersion}
                handleUploadFile={onUploadFile}
                handleRemoveFile={onRemoveFile}
                onViewDetails={goToVersion}
                isUploading={isUploading}
              />
              <Box mt={3} />
              <Comments id={parseInt(id)} type="document" />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <Logs
                targetId={parseInt(id)}
                targetType="Document"
                title="Document logs"
                height={600}
              />
            </Grid>
          </Grid>
        </Box>
      </StyledContainer>
      {createVersionModal && (
        <VersionModal
          mode={'create'}
          handleCreate={onCreate}
          open={createVersionModal}
          handleClose={toggleVersionModal}
          asyncErrors={asyncErrors.createVersion}
        />
      )}
      {renameModal && (
        <DocumentModal
          mode={'update'}
          open={renameModal}
          document_type_id={document?.document_type?.id}
          document_type_name={document?.document_type?.name}
          description={document.description}
          handleCreate={onRename}
          handleClose={toggleRenameModal}
        />
      )}
      <DeleteDocumentConfirm />
    </Layout>
  );
};

export default DocumentsDetailsPage;
